import {context, trace} from '@opentelemetry/api';
import {
  type AnyValueMap,
  type LogAttributes,
  logs,
  SeverityNumber,
} from '@opentelemetry/api-logs';
import type pino from 'pino';

import {setLoggerTelemetryBridge} from '../utils/logger/bridge';

import {
  INVALID_SPAN_ID,
  INVALID_TRACE_ID,
  LOG_ATTRIBUTE_COUNT_LIMIT,
  LOG_EXPORT_TIMEOUT_MS,
  LOG_EXPORTER_CONCURRENCY_LIMIT,
  LOG_SCHEDULED_DELAY_MS,
  OTEL_LOGGER_STATE,
  OTEL_LOGS_PATH,
  PENDING_SDK_LOG_LIMIT,
} from './constants';
import type {
  InitializeOpenTelemetryLoggerOptions,
  OpenTelemetryLogRecord,
  OtlpAttributeValue,
  OtlpKeyValue,
  OtlpLogsPayload,
  RuntimeGlobals,
  RuntimeNavigator,
  RuntimePerformance,
} from './types';

const isBrowserRuntime = (): boolean => {
  return typeof window !== 'undefined';
};

const isOtelLogDiagnosticsEnabled = (): boolean => {
  return (
    process.env.OTEL_LOG_DIAGNOSTICS === 'true' ||
    process.env.NEXT_PUBLIC_OTEL_LOG_DIAGNOSTICS === 'true' ||
    (
      globalThis as {
        __ZOE_OTEL_LOG_DIAGNOSTICS__?: boolean;
      }
    ).__ZOE_OTEL_LOG_DIAGNOSTICS__ === true
  );
};

const emitOtelLogDiagnostic = (
  level: 'error' | 'info' | 'warn',
  message: string,
  extra?: Record<string, unknown>,
): void => {
  if (!isOtelLogDiagnosticsEnabled()) {
    return;
  }

  const payload = extra ? {message, ...extra} : {message};

  console[level]('[zoe-otel-logs]', payload);
};

const getRuntimeName = (): string => {
  if (isBrowserRuntime()) {
    return 'browser';
  }

  if (process.env.NEXT_RUNTIME) {
    return process.env.NEXT_RUNTIME;
  }

  return isEdgeLikeRuntime() ? 'edge' : 'node';
};

/** Resolves the host identifier for the `host.name` OTLP resource attribute. */
const getHostName = (): string | undefined => {
  if (isBrowserRuntime()) {
    return globalThis.location?.hostname;
  }

  return process.env.VERCEL_URL || process.env.HOSTNAME;
};

/** Detects Vercel Edge Runtime or Next.js edge middleware context. */
const isEdgeLikeRuntime = (): boolean => {
  return (
    typeof (globalThis as {EdgeRuntime?: unknown}).EdgeRuntime !==
      'undefined' || process.env.NEXT_RUNTIME === 'edge'
  );
};

/**
 * Polyfills `performance.now` and `navigator.sendBeacon` for Node-ish runtimes
 * where the OTEL SDK log exporter expects browser globals to exist.
 */
const ensureOtelGlobals = (): void => {
  const runtime = globalThis as RuntimeGlobals;

  if (!runtime.performance) {
    runtime.performance = {} as RuntimePerformance;
  }

  if (!runtime.performance.now) {
    const nowOffset = runtime.performance.timing?.navigationStart ?? Date.now();

    runtime.performance.now = function now() {
      return Date.now() - nowOffset;
    };
  }

  if (!runtime.navigator) {
    runtime.navigator = {} as RuntimeNavigator;
  }

  if (!runtime.navigator.sendBeacon) {
    runtime.navigator.sendBeacon = (url: string, body: BodyInit) => {
      fetch(url, {
        body,
        keepalive: true,
        method: 'POST',
        // Annotated: consumers without strictNullChecks infer `any` here.
      }).catch((): void => undefined);

      return true;
    };
  }
};

/** Joins a collector host with a path, producing a full URL string. */
const resolveCollectorEndpoint = (
  collectorHost: string,
  path: string,
): string => {
  return new URL(path, collectorHost).toString();
};

/** Recursively converts unknown values into JSON-safe primitives/structures. */
const serializeUnknown = (value: unknown): unknown => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => serializeUnknown(item));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        serializeUnknown(item),
      ]),
    );
  }

  return String(value);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

/** Reads traceId/spanId from the active OTel span for log–trace correlation. */
const getActiveTraceAttributes = (): LogAttributes => {
  const activeSpan = trace.getActiveSpan();
  const spanContext = activeSpan?.spanContext();

  if (
    !spanContext ||
    spanContext.traceId === INVALID_TRACE_ID ||
    spanContext.spanId === INVALID_SPAN_ID
  ) {
    return {};
  }

  return {
    trace_id: spanContext.traceId,
    span_id: spanContext.spanId,
    trace_flags: spanContext.traceFlags,
  };
};

/** Maps a pino numeric level to the OTLP SeverityNumber enum. */
const getSeverityNumber = (level: number): SeverityNumber => {
  if (level >= 60) return SeverityNumber.FATAL;
  if (level >= 50) return SeverityNumber.ERROR;
  if (level >= 40) return SeverityNumber.WARN;
  if (level >= 30) return SeverityNumber.INFO;
  if (level >= 20) return SeverityNumber.DEBUG;

  return SeverityNumber.TRACE;
};

/** Maps a pino numeric level to the OTLP severity text label. */
const getSeverityText = (level: number): string => {
  if (level >= 60) return 'FATAL';
  if (level >= 50) return 'ERROR';
  if (level >= 40) return 'WARN';
  if (level >= 30) return 'INFO';
  if (level >= 20) return 'DEBUG';

  return 'TRACE';
};

/** Normalises pino log arguments into an OTLP-shaped record with body + attributes. */
const extractLogRecord = (
  args: Parameters<pino.LogFn>,
  level: number,
): OpenTelemetryLogRecord => {
  const attributes: AnyValueMap = {
    ...getActiveTraceAttributes(),
  };
  const messageParts: string[] = [];

  for (const arg of args) {
    if (arg instanceof Error) {
      attributes.error = serializeUnknown(arg) as AnyValueMap[string];

      if (!messageParts.length && arg.message) {
        messageParts.push(arg.message);
      }

      continue;
    }

    if (isPlainObject(arg)) {
      Object.assign(attributes, serializeUnknown(arg));
      continue;
    }

    if (
      typeof arg === 'string' ||
      typeof arg === 'number' ||
      typeof arg === 'boolean'
    ) {
      messageParts.push(String(arg));
      continue;
    }

    if (arg !== undefined) {
      messageParts.push(String(serializeUnknown(arg)));
    }
  }

  return {
    attributes: {
      ...attributes,
      'log.level_num': level,
    },
    body: messageParts.join(' ').trim() || 'Log event',
    context: context.active(),
  };
};

/** Wraps a JS value into the OTLP AnyValue one-of envelope. */
const toOtlpAttributeValue = (value: unknown): OtlpAttributeValue => {
  if (typeof value === 'boolean') return {boolValue: value};

  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? {intValue: String(value)}
      : {doubleValue: value};
  }

  if (typeof value === 'object') {
    return {stringValue: JSON.stringify(value)};
  }

  return {stringValue: String(value ?? '')};
};

/** Converts a flat key/value map into the OTLP repeated KeyValue format. */
const toOtlpAttributes = (attrs: Record<string, unknown>): OtlpKeyValue[] => {
  return Object.entries(attrs)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([key, value]) => ({key, value: toOtlpAttributeValue(value)}));
};

/** Converts a Unix-ms timestamp to the nanosecond string OTLP expects. */
const msToNanosecondString = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const remainder = ms % 1000;

  return `${seconds}${String(remainder).padStart(3, '0')}000000`;
};

/**
 * Drains the direct-export log buffer into a single OTLP HTTP/JSON POST.
 * Errors are silently swallowed so logging never breaks the request.
 */
const flushEdgeLogs = async (): Promise<void> => {
  if (
    !OTEL_LOGGER_STATE.edgeExportConfig ||
    OTEL_LOGGER_STATE.edgeLogBuffer.length === 0
  ) {
    return;
  }

  const records = OTEL_LOGGER_STATE.edgeLogBuffer;
  const config = OTEL_LOGGER_STATE.edgeExportConfig;

  OTEL_LOGGER_STATE.edgeLogBuffer = [];

  const payload: OtlpLogsPayload = {
    resourceLogs: [
      {
        resource: {
          attributes: toOtlpAttributes({
            'service.name': config.serviceName,
            ...(config.serviceVersion
              ? {'service.version': config.serviceVersion}
              : {}),
            'deployment.environment': config.environment || 'local',
            ...(config.hostName ? {'host.name': config.hostName} : {}),
            'process.runtime.name': config.runtimeName,
          }),
        },
        scopeLogs: [
          {
            scope: {name: config.serviceName},
            logRecords: records.map(({record, level, timestampMs}) => ({
              timeUnixNano: msToNanosecondString(timestampMs),
              severityNumber: getSeverityNumber(level),
              severityText: getSeverityText(level),
              body: {stringValue: record.body},
              attributes: toOtlpAttributes(record.attributes),
              ...(record.attributes.trace_id
                ? {traceId: String(record.attributes.trace_id)}
                : {}),
              ...(record.attributes.span_id
                ? {spanId: String(record.attributes.span_id)}
                : {}),
            })),
          },
        ],
      },
    ],
  };

  emitOtelLogDiagnostic('info', 'Flushing direct OTEL logs', {
    endpoint: config.endpoint,
    records: records.length,
    runtime: getRuntimeName(),
  });

  try {
    const response = await fetch(config.endpoint, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
      body: JSON.stringify(payload),
    });

    emitOtelLogDiagnostic('info', 'Direct OTEL log flush completed', {
      endpoint: config.endpoint,
      records: records.length,
      runtime: getRuntimeName(),
      status: response.status,
    });
  } catch (error) {
    emitOtelLogDiagnostic('error', 'Direct OTEL log flush failed', {
      endpoint: config.endpoint,
      error: error instanceof Error ? error.message : String(error),
      records: records.length,
      runtime: getRuntimeName(),
    });
  }
};

const flushEdgeLogsSoon = (): void => {
  if (OTEL_LOGGER_STATE.edgeFlushPromise) {
    return;
  }

  OTEL_LOGGER_STATE.edgeFlushPromise = flushEdgeLogs().finally(() => {
    OTEL_LOGGER_STATE.edgeFlushPromise = undefined;
  });
};

const emitSdkLogRecord = (
  record: OpenTelemetryLogRecord,
  level: number,
): void => {
  OTEL_LOGGER_STATE.otelLogger.emit({
    attributes: record.attributes,
    body: record.body,
    context: record.context,
    severityNumber: getSeverityNumber(level),
    severityText: getSeverityText(level),
  });
  emitOtelLogDiagnostic('info', 'Emitted SDK log record', {
    body: record.body,
    runtime: getRuntimeName(),
    severityText: getSeverityText(level),
  });
};

const attemptAutoInitializeLoggerFromEnvironment = (): void => {
  if (
    OTEL_LOGGER_STATE.loggerConfigKey ||
    OTEL_LOGGER_STATE.loggerInitializationPromise ||
    isEdgeLikeRuntime() ||
    isBrowserRuntime()
  ) {
    return;
  }

  const collectorHost =
    process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT_REMOTE ||
    process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT;

  if (!collectorHost) {
    return;
  }

  const serviceName = process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || 'dashboard';
  const environment =
    process.env.NEXT_PUBLIC_OTEL_SERVICE_ENVIRONMENT ||
    process.env.NODE_ENV ||
    'local';

  initializeOpenTelemetryLogger({
    collectorHost,
    environment,
    serviceName,
    serviceVersion:
      process.env.VERCEL_GIT_COMMIT_SHA || process.env.APP_VERSION,
  }).catch((error) => {
    emitOtelLogDiagnostic(
      'error',
      'Auto initialization of OTEL SDK logger failed',
      {
        error: error instanceof Error ? error.message : String(error),
        runtime: getRuntimeName(),
      },
    );
  });
};

/**
 * Routes a pino log call into OTLP.
 * On browser/edge: queues the record for direct OTLP HTTP flush via fetch.
 * On Node: emits through the SDK LoggerProvider.
 */
const emitOpenTelemetryLog = (
  args: Parameters<pino.LogFn>,
  level: number,
): void => {
  const record = extractLogRecord(args, level);

  if (OTEL_LOGGER_STATE.edgeExportConfig) {
    OTEL_LOGGER_STATE.edgeLogBuffer.push({
      record,
      level,
      timestampMs: Date.now(),
    });
    emitOtelLogDiagnostic('info', 'Queued direct OTEL log record', {
      body: record.body,
      bufferedRecords: OTEL_LOGGER_STATE.edgeLogBuffer.length,
      runtime: getRuntimeName(),
      severityText: getSeverityText(level),
    });
    flushEdgeLogsSoon();

    return;
  }

  if (OTEL_LOGGER_STATE.loggerProvider) {
    emitSdkLogRecord(record, level);

    return;
  }

  attemptAutoInitializeLoggerFromEnvironment();

  OTEL_LOGGER_STATE.pendingSdkLogBuffer.push({record, level});

  if (OTEL_LOGGER_STATE.pendingSdkLogBuffer.length > PENDING_SDK_LOG_LIMIT) {
    OTEL_LOGGER_STATE.pendingSdkLogBuffer =
      OTEL_LOGGER_STATE.pendingSdkLogBuffer.slice(-PENDING_SDK_LOG_LIMIT);
  }

  if (OTEL_LOGGER_STATE.hasWarnedMissingLoggerProvider) {
    return;
  }

  OTEL_LOGGER_STATE.hasWarnedMissingLoggerProvider = true;
  emitOtelLogDiagnostic('warn', 'OpenTelemetry log buffered before init', {
    hasEdgeConfig: Boolean(OTEL_LOGGER_STATE.edgeExportConfig),
    hasInitializationPromise: Boolean(
      OTEL_LOGGER_STATE.loggerInitializationPromise,
    ),
    pendingBufferedRecords: OTEL_LOGGER_STATE.pendingSdkLogBuffer.length,
    runtime: getRuntimeName(),
  });
};

/**
 * Sets up the OTLP log pipeline for the current runtime:
 * - Browser/edge: stores config for a lightweight fetch-based exporter.
 * - Node: lazily imports the full OTEL SDK and creates a log processor.
 *
 * Idempotent — re-calling with the same options is a no-op.
 */
export const initializeOpenTelemetryLogger = ({
  collectorHost,
  environment,
  headers,
  serviceName,
  serviceVersion,
}: InitializeOpenTelemetryLoggerOptions): Promise<void> => {
  if (!collectorHost) {
    emitOtelLogDiagnostic('warn', 'Skipped OTEL log initialization', {
      reason: 'missing-collector-host',
    });

    return Promise.resolve();
  }

  const logsEndpoint = resolveCollectorEndpoint(collectorHost, OTEL_LOGS_PATH);
  const hostName = getHostName();
  const runtimeName = getRuntimeName();
  const configKey = JSON.stringify({
    collectorHost,
    environment,
    headers,
    serviceName,
    serviceVersion,
  });

  const usesDirectExporter = isEdgeLikeRuntime() || isBrowserRuntime();
  const alreadyConfigured = OTEL_LOGGER_STATE.loggerConfigKey === configKey;

  if (
    usesDirectExporter &&
    alreadyConfigured &&
    OTEL_LOGGER_STATE.edgeExportConfig
  ) {
    return Promise.resolve();
  }

  if (usesDirectExporter) {
    OTEL_LOGGER_STATE.loggerConfigKey = configKey;
    OTEL_LOGGER_STATE.edgeExportConfig = {
      endpoint: logsEndpoint,
      headers,
      hostName,
      runtimeName,
      serviceName,
      serviceVersion,
      environment,
    };
    OTEL_LOGGER_STATE.hasWarnedMissingLoggerProvider = false;
    emitOtelLogDiagnostic('info', 'Configured OTEL direct logger', {
      endpoint: logsEndpoint,
      runtime: getRuntimeName(),
      serviceName,
      serviceVersion,
    });

    // Drain the buffered log records to the edge exporter
    // This is necessary because the edge exporter is not initialized until the logger is initialized
    // and we want to ensure that all log records are flushed to the edge exporter as soon as possible
    if (OTEL_LOGGER_STATE.pendingSdkLogBuffer.length > 0) {
      const bufferedRecords = OTEL_LOGGER_STATE.pendingSdkLogBuffer;

      OTEL_LOGGER_STATE.pendingSdkLogBuffer = [];
      bufferedRecords.forEach(({record, level}) => {
        OTEL_LOGGER_STATE.edgeLogBuffer.push({
          record,
          level,
          timestampMs: Date.now(),
        });
      });
      emitOtelLogDiagnostic(
        'info',
        'Drained buffered log records to edge exporter',
        {
          records: bufferedRecords.length,
          runtime: getRuntimeName(),
        },
      );
      flushEdgeLogsSoon();
    }

    return Promise.resolve();
  }

  if (alreadyConfigured && OTEL_LOGGER_STATE.loggerProvider) {
    return Promise.resolve();
  }

  if (alreadyConfigured && OTEL_LOGGER_STATE.loggerInitializationPromise) {
    return OTEL_LOGGER_STATE.loggerInitializationPromise;
  }

  ensureOtelGlobals();
  OTEL_LOGGER_STATE.loggerConfigKey = configKey;
  emitOtelLogDiagnostic('info', 'Initializing OTEL SDK logger', {
    endpoint: logsEndpoint,
    runtime: getRuntimeName(),
    serviceName,
    serviceVersion,
  });

  OTEL_LOGGER_STATE.loggerInitializationPromise = (async () => {
    // Dynamic imports: these SDK packages depend on Node internals and would
    // crash on edge at module-evaluation time. This module is loaded in both
    // runtimes via instrumentation.ts, so they must stay lazy.
    const [{OTLPLogExporter}, {resourceFromAttributes}, sdkLogsModule] =
      await Promise.all([
        import('@opentelemetry/exporter-logs-otlp-http'),
        import('@opentelemetry/resources'),
        import('@opentelemetry/sdk-logs'),
      ]);
    const {BatchLogRecordProcessor, LoggerProvider, SimpleLogRecordProcessor} =
      sdkLogsModule;
    const exporter = new OTLPLogExporter({
      concurrencyLimit: LOG_EXPORTER_CONCURRENCY_LIMIT,
      headers,
      timeoutMillis: LOG_EXPORT_TIMEOUT_MS,
      url: logsEndpoint,
    });
    const processor = isBrowserRuntime()
      ? new BatchLogRecordProcessor({
          exporter,
          scheduledDelayMillis: LOG_SCHEDULED_DELAY_MS,
        })
      : new SimpleLogRecordProcessor({exporter});

    OTEL_LOGGER_STATE.loggerProvider = new LoggerProvider({
      logRecordLimits: {
        attributeCountLimit: LOG_ATTRIBUTE_COUNT_LIMIT,
        attributeValueLengthLimit: Infinity,
      },
      processors: [processor],
      resource: resourceFromAttributes({
        'deployment.environment': environment || 'local',
        'service.name': serviceName,
        ...(serviceVersion ? {'service.version': serviceVersion} : {}),
        ...(hostName ? {'host.name': hostName} : {}),
        'process.runtime.name': runtimeName,
      }),
    });
    OTEL_LOGGER_STATE.otelLogger = OTEL_LOGGER_STATE.loggerProvider.getLogger(
      serviceName,
      serviceVersion,
    );

    logs.setGlobalLoggerProvider(OTEL_LOGGER_STATE.loggerProvider);
    OTEL_LOGGER_STATE.hasWarnedMissingLoggerProvider = false;

    if (OTEL_LOGGER_STATE.pendingSdkLogBuffer.length > 0) {
      const bufferedRecords = OTEL_LOGGER_STATE.pendingSdkLogBuffer;

      OTEL_LOGGER_STATE.pendingSdkLogBuffer = [];
      bufferedRecords.forEach(({record, level}) => {
        emitSdkLogRecord(record, level);
      });
      emitOtelLogDiagnostic('info', 'Drained buffered SDK log records', {
        records: bufferedRecords.length,
        runtime: getRuntimeName(),
      });
    }

    emitOtelLogDiagnostic('info', 'Initialized OTEL SDK logger', {
      endpoint: logsEndpoint,
      processor: isBrowserRuntime() ? 'batch' : 'simple',
      runtime: getRuntimeName(),
      serviceName,
      serviceVersion,
    });
  })()
    .catch((error) => {
      OTEL_LOGGER_STATE.loggerConfigKey = undefined;
      OTEL_LOGGER_STATE.loggerProvider = undefined;
      emitOtelLogDiagnostic('error', 'Failed to initialize OTEL SDK logger', {
        endpoint: logsEndpoint,
        error: error instanceof Error ? error.message : String(error),
        runtime: getRuntimeName(),
        serviceName,
        serviceVersion,
      });
      throw error;
    })
    .finally(() => {
      OTEL_LOGGER_STATE.loggerInitializationPromise = undefined;
    });

  return OTEL_LOGGER_STATE.loggerInitializationPromise;
};

/** Flushes pending log records — drains the edge buffer or the SDK provider. */
export const forceFlushOpenTelemetryLogger = async (): Promise<void> => {
  if (!OTEL_LOGGER_STATE.edgeExportConfig) {
    emitOtelLogDiagnostic('info', 'Force flushing OTEL SDK logger', {
      runtime: getRuntimeName(),
    });

    await OTEL_LOGGER_STATE.loggerProvider?.forceFlush().catch((error) => {
      emitOtelLogDiagnostic('error', 'Failed to force flush OTEL SDK logger', {
        error: error instanceof Error ? error.message : String(error),
        runtime: getRuntimeName(),
      });
    });

    return;
  }

  if (OTEL_LOGGER_STATE.edgeFlushPromise) {
    await OTEL_LOGGER_STATE.edgeFlushPromise;
  }

  await flushEdgeLogs();
};

/** Returns trace identifiers so every pino line can be correlated with the active span. */
export const getOpenTelemetryLogFields = (): Record<string, unknown> => {
  return getActiveTraceAttributes();
};

export const registerOpenTelemetryLoggerBridge = (): void => {
  setLoggerTelemetryBridge({
    forwardLog: emitOpenTelemetryLog,
    getFields: getOpenTelemetryLogFields,
  });
};
